// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports
import { OffsetOptions, ShiftOptions } from "@floating-ui/dom";
import { UseHoverProps } from "@floating-ui/react/dist/floating-ui.react";

import {
    FloatingArrow,
    FloatingArrowProps,
    FloatingNode,
    FloatingPortal,
    FloatingTree,
    Placement,
    useFloatingParentNodeId,
    useTransitionStyles,
} from "@floating-ui/react";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import * as ariaAppHider from "@insite/mobius/Overlay/helpers/ariaAppHider";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { TooltipContext } from "@steiner/App/Components/FloatingTooltip/TooltipContext";
import { FocusableSelectors, useTooltip } from "@steiner/App/Components/FloatingTooltip/UseTooltip";
import xHeaderUpdate from "@steiner/App/Handlers/XNG/Header";
import { addScrollbarGutter, mergeWithCss, removeScrollbarGutter } from "@steiner/XDL/Utilities";
import * as React from "react";
import { useEffect } from "react";
import { shallowEqual, useDispatch } from "react-redux";

export type FloatingTooltipProps = {
    placement?: Placement;
    trigger?: "hover" | "click" | "manual";
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    offset?: OffsetOptions;
    shift?: ShiftOptions;
    delay?: UseHoverProps["delay"];
    showArrow?: boolean;
    overlay?: boolean;
    triggerComponent: React.ReactNode;
    enabled?: boolean;
    extendedStyles?: FloatingTooltipStyles;
};

interface Props extends FloatingTooltipProps {
    children: React.ReactNode;
}

export interface FloatingTooltipRef {
    open: () => void;
    close: () => void;
    toggle: () => void;
    isOpen: boolean;
}

export interface FloatingTooltipStyles {
    tooltipContent?: InjectableCss;
    tooltipArrow?: Omit<FloatingArrowProps, "context">;
}

export const floatingTooltipStyles: FloatingTooltipStyles = {};

const FloatingTooltipComponent = React.forwardRef<FloatingTooltipRef, Props>(
    (
        {
            children,
            enabled = true,
            triggerComponent,
            overlay = true,
            extendedStyles,
            showArrow = true,
            open: controlledOpen, // Optional controlled prop
            onOpenChange, // Optional controlled prop
            trigger = "hover",
            ...options
        },
        ref,
    ) => {
        const arrowRef = React.useRef<SVGSVGElement>(null);
        const tooltip = useTooltip({ ...options, showArrow, arrowRef, trigger, onOpenChange, open: controlledOpen });
        const { isMounted, styles: translationStyles } = useTransitionStyles(tooltip.context);

        const location = useAppSelector(o => getLocation(o), shallowEqual);
        const { isEditing } = useShellContext();

        const [styles] = React.useState(() => mergeWithCss(floatingTooltipStyles, extendedStyles));
        const dispatch = useDispatch();

        // Expose methods via ref
        React.useImperativeHandle(ref, () => ({
            open: () => tooltip.setOpen(true),
            close: () => tooltip.setOpen(false),
            toggle: () => tooltip.setOpen(!tooltip.open),
            isOpen: tooltip.open,
        }));

        useEffect(() => {
            if (!isMounted && trigger === "hover") {
                tooltip.setHoverEnabled(true);
            }
        }, [isMounted, trigger, tooltip]);

        useEffect(() => {
            if (overlay) {
                const menuKey = `${tooltip.nodeId}_tooltip`;

                const isOverlay = isMounted && (trigger !== "manual" || controlledOpen);
                dispatch(
                    xHeaderUpdate({
                        key: isOverlay ? "registerMenu" : "unregisterMenu",
                        value: menuKey,
                    }) as any,
                );
                isOverlay ? addScrollbarGutter() : removeScrollbarGutter();
                isOverlay ? ariaAppHider.hide(document.body) : ariaAppHider.show(document.body);

                // STNRS-914
                // Cleanup function to ensure proper unregistration
                return () => {
                    dispatch(
                        xHeaderUpdate({
                            key: "unregisterMenu",
                            value: menuKey,
                        }) as any,
                    );
                    removeScrollbarGutter();
                    ariaAppHider.show(document.body);
                };
            }
        }, [isMounted, controlledOpen]);

        /**
         * Close Menu when the location change
         */
        useEffect(() => {
            tooltip.setOpen(false);
            onOpenChange?.(false);
            if (overlay) {
                dispatch(
                    xHeaderUpdate({
                        key: "unregisterMenu",
                        value: `${tooltip.nodeId}_tooltip`,
                    }) as any,
                );
            }
        }, [location.pathname]);

        if (!enabled || isEditing) {
            return <>{triggerComponent}</>;
        }

        return (
            <FloatingNode id={tooltip.nodeId}>
                <TooltipContext.Provider value={{ ...tooltip, open: isMounted }}>
                    <span
                        ref={tooltip.refs.setReference}
                        {...tooltip.getReferenceProps()}
                        data-state={isMounted ? "open" : "closed"}
                    >
                        {triggerComponent}
                    </span>
                    <FloatingPortal>
                        <StyledWrapper
                            ref={tooltip.refs.setFloating}
                            style={{
                                ...tooltip.floatingStyles,
                                ...translationStyles,
                                display: isMounted ? "block" : "none", // Control visibility instead of unmounting
                            }}
                            {...styles.tooltipContent}
                            {...tooltip.getFloatingProps({
                                onFocus(event: React.FocusEvent<HTMLElement>) {
                                    const target = event.target as HTMLElement;
                                    if (
                                        (trigger === "hover" && target.matches(FocusableSelectors)) ||
                                        target.isContentEditable
                                    ) {
                                        tooltip.setHoverEnabled(false);
                                    }
                                },
                                onBlur(event: React.FocusEvent<HTMLElement>) {
                                    const target = event.target as HTMLElement;
                                    if (
                                        (trigger === "hover" && target.matches(FocusableSelectors)) ||
                                        target.isContentEditable
                                    ) {
                                        tooltip.setHoverEnabled(true);
                                    }
                                },
                            })}
                        >
                            {children}
                            {showArrow && (
                                <FloatingArrow ref={arrowRef} context={tooltip.context} {...styles.tooltipArrow} />
                            )}
                        </StyledWrapper>
                    </FloatingPortal>
                </TooltipContext.Provider>
            </FloatingNode>
        );
    },
);

export const FloatingTooltip = React.forwardRef<FloatingTooltipRef, Props>((props, ref) => {
    const parentId = useFloatingParentNodeId();
    if (parentId === null) {
        return (
            <FloatingTree>
                <FloatingTooltipComponent {...props} ref={ref} />
            </FloatingTree>
        );
    }
    return <FloatingTooltipComponent {...props} />;
});

export default FloatingTooltip;
